import AOS from "aos";
import "aos/dist/aos.css";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Cursor from "../../common/Cursor";
import Footer from "../../common/Footer";
import Header from "../../common/Header";
import Loader from "../../common/Loader";
import ProgressCircle from "../../common/ProgressCircle";
import { fetchData, toAbsoluteMedia } from "../../api/api";

// helpers
function stripHtml(html = "") {
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || el.innerText || "").trim();
}

function truncate(text = "", max = 180) {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const sp = cut.lastIndexOf(" ");
  return (sp > 0 ? cut.slice(0, sp) : cut) + "…";
}

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    AOS.init({ duration: 2000 });
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const raw = await fetchData("/career/job-postings/"); // trailing slash matters
        console.log("JOB POSTINGS RAW DATA:", raw);

        const data = Array.isArray(raw) ? raw : raw?.results || [];

        const mapped = data.map((job, idx) => {
          // Build location string from nested object
          let locationStr = "";
          if (job?.location) {
            const parts = [];
            if (job.location.city) parts.push(job.location.city);
            if (job.location.country) parts.push(job.location.country);
            if (job.location.is_remote) parts.push("Remote");
            locationStr = parts.join(", ");
          }

          // Get job type from nested object
          const jobType = job?.job_type?.type_name || "";

          // Get team/department name
          const department = job?.team?.name || "";

          // Build salary string
          const salary = job?.salary_range || "";

          const item = {
            id: job?.id || idx,
            title: job.title || "",
            department: department,
            location: locationStr, // Now it's a string, not an object
            type: jobType, // Now it's a string, not an object
            description: truncate(stripHtml(job.short_description || job.description || ""), 200),
            requirements: job?.requirements || "",
            salary: salary,
            openings: job?.openings || null,
            experienceLevel: job?.experience_level || "",
            postedDate: job.posted_on ? new Date(job.posted_on) : null,
            applicationDeadline: job.application_deadline ? new Date(job.application_deadline) : null,
            image: toAbsoluteMedia(job?.team?.icon || job.image || ""),
            slug: job?.slug || job?.id || idx,
            applyLink: job?.apply_link || null,
            isActive: job?.is_active !== undefined ? job.is_active : true,
          };
          return item;
        });

        // sort newest first (if date), else by title
        mapped.sort((a, b) => {
          if (a.postedDate && b.postedDate) return b.postedDate - a.postedDate;
          return a.title.localeCompare(b.title);
        });

        if (alive) setJobs(mapped);
      } catch (e) {
        console.error("Error fetching job postings:", e);
        if (alive) setErr("Failed to load job postings.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <>
      <Helmet>
        <title>Careers | Marichi Tech AI</title>
        <meta
          name="description"
          content="Join Marichi Tech AI and be a part of our journey in building smart digital solutions."
        />
        <meta
          name="keywords"
          content="Careers, tech jobs, software jobs, hiring developers, IT jobs"
        />
        <meta property="og:title" content="Careers | Marichi Tech AI" />
        <meta
          property="og:description"
          content="We're hiring! Explore exciting opportunities in web, mobile, and cloud development."
        />
        <meta
          property="og:image"
          content="https://example.com/career-og-image.jpg"
        />
      </Helmet>

      <div>
        {/* Cursor */}
        <Cursor />
        {/* Header */}
        <Header />

        {/* Main Section Header */}
        <section
          style={{
            padding: "60px 0 40px",
            minHeight: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              borderRadius: "12px",
              padding: "40px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              textAlign: "center",
              maxWidth: "800px",
              width: "100%",
            }}
            data-aos="fade-up"
          >
            <h1
              style={{
                fontSize: "clamp(28px, 5vw, 36px)",
                fontWeight: "700",
                color: "white",
                lineHeight: "1.4",
                marginBottom: "16px",
              }}
              data-aos="fade-up"
              data-aos-delay="100"
            >
              Join Our Team
            </h1>
            <p
              style={{
                fontSize: "18px",
                color: "white",
                lineHeight: "1.7",
                marginBottom: "24px",
              }}
              data-aos="fade-up"
              data-aos-delay="200"
            >
              We're always on the lookout for talented individuals to join our
              dynamic team. Explore our current job openings below.
            </p>
          </div>
        </section>

        {/* Job Postings List Section */}
        {loading && <Loader />}
        
        <section
          style={{
            padding: "40px 0 60px",
            minHeight: "50vh",
          }}
        >
          <div className="container-fluid" style={{ paddingLeft: 24, paddingRight: 24 }}>
            {!loading && err && (
              <div style={{ padding: "40px", textAlign: "center" }}>
                <p style={{ color: "red", fontSize: "18px" }}>{err}</p>
                <p style={{ color: "white", fontSize: "16px", marginTop: "16px" }}>
                  Please check back later or feel free to send us your resume for future consideration.
                </p>
                <a
                  href="mailto:info@marichitechai.com"
                  style={{
                    display: "inline-block",
                    marginTop: "20px",
                    padding: "12px 32px",
                    backgroundColor: "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "16px",
                    fontWeight: "500",
                    textDecoration: "none",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#1d4ed8";
                    e.target.style.transform = "translateY(-2px)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "#2563eb";
                    e.target.style.transform = "translateY(0)";
                  }}
                >
                  Contact Us
                </a>
              </div>
            )}

            {!loading && !err && jobs.length === 0 && (
              <div style={{ padding: "40px", textAlign: "center" }}>
                <p
                  style={{
                    fontSize: "18px",
                    color: "white",
                    lineHeight: "1.7",
                    marginBottom: "24px",
                  }}
                >
                  Unfortunately, there are <strong>no vacancies</strong> at the moment.
                </p>
                <p
                  style={{
                    fontSize: "16px",
                    color: "white",
                    lineHeight: "1.7",
                    marginBottom: "32px",
                  }}
                >
                  Please check back later for exciting career opportunities, or feel
                  free to send us your resume for future consideration.
                </p>
                <a
                  href="mailto:info@marichitechai.com"
                  style={{
                    display: "inline-block",
                    padding: "12px 32px",
                    backgroundColor: "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "16px",
                    fontWeight: "500",
                    textDecoration: "none",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#1d4ed8";
                    e.target.style.transform = "translateY(-2px)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "#2563eb";
                    e.target.style.transform = "translateY(0)";
                  }}
                >
                  Contact Us
                </a>
              </div>
            )}

            {!loading && !err && jobs.length > 0 && (
              <div>
                <h2
                  style={{
                    fontSize: "clamp(24px, 4vw, 32px)",
                    fontWeight: "700",
                    color: "white",
                    textAlign: "center",
                    marginBottom: "40px",
                  }}
                  data-aos="fade-up"
                >
                  Current Openings
                </h2>
                {jobs.map((job, i) => (
                  <div
                    className="list-blog-main"
                    data-aos="fade-up"
                    key={job.id || i}
                    // override global `.list-blog-main` (it's flex w/ huge gap)
                    style={{ marginBottom: "22px", width: "100%", display: "block", padding: 0, border: "none" }}
                  >
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "16px",
                          padding: "16px 18px",
                          borderRadius: "12px",
                          background: "rgba(255, 255, 255, 0.03)",
                          border: "1px solid rgba(255, 255, 255, 0.10)",
                          width: "100%",
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 }}>
                          <div style={{ color: "white", fontWeight: 700, fontSize: "18px" }}>
                            {job.title}
                          </div>

                          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                            {job.location && (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  padding: "6px 10px",
                                  borderRadius: "999px",
                                  background: "rgba(96, 165, 250, 0.10)",
                                  border: "1px solid rgba(96, 165, 250, 0.25)",
                                  color: "#93c5fd",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                }}
                              >
                                {job.location}
                              </span>
                            )}
                            {job.type && (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  padding: "6px 10px",
                                  borderRadius: "999px",
                                  background: "rgba(34, 197, 94, 0.10)",
                                  border: "1px solid rgba(34, 197, 94, 0.25)",
                                  color: "#86efac",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                }}
                              >
                                {job.type}
                              </span>
                            )}
                          </div>
                        </div>

                        <Link
                          to={`/career/apply/${job.id}`}
                          style={{
                            textDecoration: "none",
                            padding: "10px 14px",
                            borderRadius: "10px",
                            background: "#2563eb",
                            color: "white",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            marginLeft: "auto",
                          }}
                        >
                          Apply
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Footer */}
        <Footer />
        {/* Progress Circle */}
        <ProgressCircle />
      </div>
    </>
  );
};

export default Careers;
